import { put, call, take, all, select } from "redux-saga/effects";
import makeRequest from "rdx/utils/makeRequest";
import getErrorActions from "rdx/utils/getErrorActions";
import { actions, selectors } from "rdx";
import { STAGES } from "lib/misc/globalVars";
import types from "rdx/types";
import { providerLeadsApi } from "rdx/modules/projects/endpoints";
import { orgProjectsApi } from "rdx/modules/enterpriseProjects/endpoints";
import { ORG_PROJECTS_TAGS } from "rdx/modules/enterpriseProjects/tags";
import { PROJECT_TAGS } from "rdx/modules/projects/tags";
import { leadsApi } from "rdx/modules/leads/endpoints";
import { LEADS_TAGS } from "rdx/modules/leads/tags";
import messages from "../messages";
import pollVisionProposal from "./pollVisionProposal";

const successAlertEffects = (message) => [
  put(actions.newSuccessEvent({ message })),
  put(actions.setAlertMessageVisible({ message })),
];

const proposalListRefreshEffects = () => [
  put(orgProjectsApi.util.invalidateTags([ORG_PROJECTS_TAGS.ORG_PRE_SALES_PROJECTS])),
  put(providerLeadsApi.util.invalidateTags([PROJECT_TAGS.PROJECT_ACTIONS])),
  put(leadsApi.util.invalidateTags([LEADS_TAGS.LEADS_TABLE])),
];

function* requestVisionProposal(action) {
  const { id, requestType, proposalNotes, cloneId, displayName, designTool, requestedTurnaround, product, enterprise } =
    action.payload;

  yield put(actions.getVisionProposalCount({ id }));
  const { payload: initialCount } = yield take(types.SET_VISION_PROPOSAL_COUNT) || 0;
  const { providerLead } = yield select(selectors.getProjectDetails);

  const { success, data, error } = yield* makeRequest.post(`/provider_leads/${id}/request_vision_proposal`, {
    request_type: requestType || "over-night",
    proposal_notes: proposalNotes,
    clone_id: cloneId,
    display_name: displayName,
    design_tool: designTool,
    requested_turnaround: requestedTurnaround,
    product,
  });

  if (!success) {
    if (requestType === "self-request") {
      yield put(actions.setCreateVisionReqSuccessful(false));
    }
    return getErrorActions({
      error,
      message: messages.ERROR_REQUESTING_PROPOSAL,
    });
  }

  const linkedProjectId = data?.linked_project_id;
  if (linkedProjectId) {
    yield* trackLinkedProjectProposal({ id, linkedProjectId, enterprise });
    return null;
  }

  const pollSuccess = yield call(pollVisionProposal, { projectId: id, initialCount });
  if (pollSuccess) {
    yield all([
      ...successAlertEffects(
        cloneId ? messages.CLONE_VISION_PROPOSAL_SUCCESS : messages.REQUEST_VISION_PROPOSAL_SUCCESS,
      ),
      put(
        actions.updateProjectDetails({ projectProgress: [...providerLead.projectProgress, STAGES.PROPOSAL_REQUESTED] }),
      ),
      put(providerLeadsApi.util.invalidateTags([PROJECT_TAGS.PROJECT_PROPOSALS])),
      put(actions.getProjectProposals({ id })),
      ...proposalListRefreshEffects(),
      ...(product ? [put(actions.getProviderLeadDetails({ providerLeadId: id, enterprise }))] : []),
    ]);
  }

  return null;
}

function* trackLinkedProjectProposal({ id, linkedProjectId, enterprise }) {
  yield put(actions.setLeadToProposalDrawerLinkedProjectId(linkedProjectId));
  yield put(actions.getProviderLeadDetails({ providerLeadId: id, enterprise }));

  const pollSuccess = yield call(pollVisionProposal, { projectId: linkedProjectId, initialCount: 0 });
  if (!pollSuccess) return;

  const latestVisionProposal = yield select(selectors.selectLatestVisionProposal);
  yield all([
    put(actions.updateL2pProjectDetails({ visionProposal: latestVisionProposal })),
    ...successAlertEffects(messages.REQUEST_VISION_PROPOSAL_SUCCESS),
    put(actions.setLeadToProposalDrawerProposalRequested()),
    ...proposalListRefreshEffects(),
  ]);
}

export default requestVisionProposal;
